import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/apiConfig';
import { trackFeatureUsage } from '../../utils/analytics';
import './TradesPromo.css';

function formatCompactTime(timestamp) {
  const now = Date.now();
  const diffMs = now - new Date(timestamp).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  return `${Math.floor(diffHours / 24)}d`;
}

function formatCompactDollar(value) {
  if (!value) return '$0';
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${Math.round(value / 1e3)}K`;
  return `$${Math.round(value)}`;
}

function extractCoin(symbol) {
  return (symbol || '').replace(/USDT$|USDC$|USD$|PERP$/i, '').toUpperCase();
}

function SkeletonRow() {
  return (
    <div className="promo-trade-row promo-skeleton-row">
      <span className="promo-coin skeleton-bone" />
      <span className="promo-amount skeleton-bone" />
      <span className="promo-side-badge skeleton-bone" />
      <span className="promo-time skeleton-bone" />
    </div>
  );
}

function SkeletonNewsRow() {
  return (
    <div className="promo-news-item promo-skeleton-row">
      <span className="skeleton-bone promo-news-title-bone" />
      <span className="skeleton-bone promo-news-meta-bone" />
    </div>
  );
}

function PromoColumn({ title, icon, trades, loading, invertColors }) {
  return (
    <div className="promo-column">
      <div className="promo-column-header">
        <span className="promo-column-icon">{icon}</span>
        <span className="promo-column-title">{title}</span>
      </div>
      <div className="promo-column-list">
        {loading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : (
          trades.map((trade, i) => {
            const side = trade.side === 'buy' ? 'long' : 'short';
            const colorClass = invertColors ? (side === 'long' ? 'short' : 'long') : side;
            return (
              <div className="promo-trade-row" key={i}>
                <span className="promo-coin">{extractCoin(trade.symbol)}</span>
                <span className="promo-amount">{formatCompactDollar(trade.dollarSize)}</span>
                <span className={`promo-side-badge ${colorClass}`}>
                  {side.toUpperCase()}
                </span>
                <span className="promo-time">{formatCompactTime(trade.timestamp)}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function NewsColumn({ articles, loading }) {
  return (
    <div className="promo-column promo-column-news">
      <div className="promo-column-header">
        <span className="promo-column-icon">📰</span>
        <span className="promo-column-title">Latest News</span>
      </div>
      <div className="promo-column-list promo-news-list">
        {loading ? (
          <>
            <SkeletonNewsRow />
            <SkeletonNewsRow />
            <SkeletonNewsRow />
          </>
        ) : (
          articles.map((article, i) => (
            <div className="promo-news-item" key={i}>
              <span className="promo-news-title">{article.title}</span>
              <div className="promo-news-meta">
                {article.source && <span className="promo-news-source">{article.source}</span>}
                <span className="promo-news-time">{formatCompactTime(article.pubDate)}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function TradesPromo() {
  const [whales, setWhales] = useState([]);
  const [liquidations, setLiquidations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    // Fetch trades + summary in parallel
    Promise.all([
      fetch(`${API_BASE_URL}/api/recent-trades?limit=20&hours=24`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/whale-summary`).then(r => r.json()),
    ])
      .then(([tradesData, summaryData]) => {
        if (tradesData.success && tradesData.trades) {
          setWhales(tradesData.trades.filter(t => !t.isLiquidation).slice(0, 4));
          setLiquidations(tradesData.trades.filter(t => t.isLiquidation).slice(0, 4));
        }
        if (summaryData.success && summaryData.data) {
          setSummary(summaryData.data);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // Fetch news separately
    fetch(`${API_BASE_URL}/api/coin/BTC/news?limit=5&hours=48`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.articles) {
          setNews(data.articles.slice(0, 4));
        }
        setNewsLoading(false);
      })
      .catch(() => setNewsLoading(false));
  }, []);

  if (!loading && whales.length === 0 && liquidations.length === 0) return null;

  const whaleVol = summary?.whaleTrades;
  const liqVol = summary?.liquidations;

  return (
    <div className="trades-promo">
      <div className="trades-promo-columns">
        <PromoColumn title="Whale Trades" icon="🐋" trades={whales} loading={loading} />
        <PromoColumn title="Liquidations" icon="💥" trades={liquidations} loading={loading} invertColors />
        <NewsColumn articles={news} loading={newsLoading} />
      </div>
      <div className="trades-promo-footer">
        <div className="trades-promo-stats">
          {whaleVol && (
            <div className="promo-stat">
              <span className="promo-stat-label">Whale Volume (24h)</span>
              <span className="promo-stat-value">{formatCompactDollar(whaleVol.totalVolume)}</span>
              <span className="promo-stat-breakdown">
                <span className="promo-stat-long">Long {formatCompactDollar(whaleVol.buyVolume)}</span>
                <span className="promo-stat-short">Short {formatCompactDollar(whaleVol.sellVolume)}</span>
              </span>
            </div>
          )}
          {liqVol && (
            <div className="promo-stat">
              <span className="promo-stat-label">Liquidations (24h)</span>
              <span className="promo-stat-value">{formatCompactDollar(liqVol.totalVolume)}</span>
              <span className="promo-stat-breakdown">
                <span className="promo-stat-long">Long {formatCompactDollar(liqVol.longVolume)}</span>
                <span className="promo-stat-short">Short {formatCompactDollar(liqVol.shortVolume)}</span>
              </span>
            </div>
          )}
        </div>
        <Link to="/trades" className="trades-promo-cta" onClick={() => trackFeatureUsage('trades_promo_cta_click', { source: 'insight_article' })}>
          Track Live Whale Moves
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </Link>
      </div>
    </div>
  );
}

export default TradesPromo;
